import { useLoad } from "@redotech/react-util/load";
import { URLVisibilityType } from "@redotech/redo-model/signup-form";
import { memo, useCallback, useEffect, useState } from "react";
import { MerchantCustomizableModal } from "../../storefront/merchant-customizable-modal";
import { CustomFieldProvider } from "../form-elements/context";
import { SignupForm } from "../signup-form";
import { SignupFormProps } from "./types";
import {
  generateAnonymousCustomerId,
  getNextStepIndex,
  isOnMobile,
  parseLocalData,
  submitFormStep,
} from "./utils";

const EXPIRATION_PERIOD = 24 * 60 * 60 * 1000;
const EXIT_INTENT_LOCAL_STORAGE_KEY = "redo.marketing-exit-intent-form";

export interface ExitIntentFormLocalData {
  customerHash?: string;
  formViewedAt?: Date;
  formId?: string;
  lastTriggeredAt?: Date;
  atLeastOneStepSubmittedAt?: Date;
}

export const ExitIntentPopupFormInstance = memo(
  function ExitIntentPopupFormInstance(props: SignupFormProps) {
    const [open, setOpen] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [localData, setLocalData] = useLocalStorage<ExitIntentFormLocalData>(
      EXIT_INTENT_LOCAL_STORAGE_KEY,
      {},
    );

    useEffect(() => {
      if (!localData.customerHash) {
        const customerHash =
          props.shopifyCustomerId ?? generateAnonymousCustomerId();
        setLocalData((prev) => ({ ...prev, customerHash }));
      }
    }, [localData.customerHash, props.shopifyCustomerId, setLocalData]);

    const client = props.rpcClient;

    // Load exit intent forms only
    const formLoad = useLoad(
      async (signal) => {
        if (!client || !localData.customerHash) return undefined;

        const result = await client.getSignupForm(
          {
            customerHash: localData.customerHash.toString(),
            shopifyCustomerId: props.shopifyCustomerId ?? null,
            redoCustomerId: props.customerId ?? null,
            formId: null, // Always get a random exit intent form
            isOnMobile: isOnMobile(),
            site: props.site,
            utmParams: props.utmParams ?? "",
            forceId: undefined,
            exitIntentOnly: true, // Only get forms with exit intent enabled
          },
          { signal },
        );

        return result;
      },
      [
        client,
        localData.customerHash,
        props.shopifyCustomerId,
        props.utmParams,
      ],
    );

    const form = formLoad.value?.form ?? null;
    const formId = formLoad.value?.formId ?? null;
    const isValidURLtoShowForm =
      form?.behavior.urlVisibility.type === URLVisibilityType.INCLUSIVE
        ? form?.behavior.urlVisibility.urls.includes(location.href)
        : !form?.behavior.urlVisibility.urls.includes(location.href);
    const isCorrectSite = props.site === form?.behavior.appearsOnSite;
    const shouldDisplay = !!(form && isCorrectSite && isValidURLtoShowForm);
    const opacityHex = form
      ? Math.floor((form.theme.overlay.opacityPercent / 100) * 255)
          .toString(16)
          .padStart(2, "0")
      : "00";

    const logAnalytics = useCallback(
      async (type: "view" | "submit") => {
        if (!localData.customerHash || !client || !formId) return;

        await client.logSignupFormAnalytics({
          idempotencyKey: localData.customerHash,
          formId,
          type,
        });

        if (type === "view") {
          setLocalData((prev: ExitIntentFormLocalData) => ({
            ...prev,
            formViewedAt: new Date(),
          }));
        } else {
          setLocalData((prev: ExitIntentFormLocalData) => ({
            ...prev,
            atLeastOneStepSubmittedAt: new Date(),
          }));
        }
      },
      [client, formId, localData.customerHash, setLocalData],
    );

    const openModal = useCallback(async () => {
      if (open) return;
      setOpen(true);
      setCurrentStep(
        getNextStepIndex(form, formLoad.value?.skipPhoneSteps ?? true, 0),
      );

      if (
        !localData.formViewedAt &&
        localData.customerHash &&
        client &&
        formId
      ) {
        await logAnalytics("view");
      }
    }, [
      open,
      localData,
      client,
      formId,
      logAnalytics,
      setOpen,
      setCurrentStep,
      form,
      formLoad.value?.skipPhoneSteps,
    ]);

    // Exit intent detection
    useEffect(() => {
      if (
        completed ||
        !form ||
        !formId ||
        !form.behavior.display.exitIntent?.enabled ||
        !isValidURLtoShowForm ||
        open
      )
        return;

      const previousTriggerExpired =
        localData.lastTriggeredAt &&
        Date.now() - localData.lastTriggeredAt.getTime() > EXPIRATION_PERIOD;

      if (localData.lastTriggeredAt && !previousTriggerExpired) return;

      let hasTriggered = false;
      let lastY = 0;
      let lastTime = Date.now();

      const handleMouseMove = (e: MouseEvent) => {
        const currentTime = Date.now();
        const timeDiff = currentTime - lastTime;
        const yDiff = e.clientY - lastY;

        // Calculate velocity (pixels per millisecond)
        const velocity = timeDiff > 0 ? yDiff / timeDiff : 0;

        // Update tracking variables
        lastY = e.clientY;
        lastTime = currentTime;

        // Detect exit intent: moving quickly upward (negative velocity) near top of page
        if (e.clientY <= 50 && velocity < -0.5 && !hasTriggered) {
          hasTriggered = true;
          setLocalData((prev: ExitIntentFormLocalData) => ({
            ...prev,
            lastTriggeredAt: new Date(),
          }));
          openModal().catch((e) =>
            console.error("Failed to open exit intent form", e),
          );
        }
      };

      const handleMouseLeave = (e: MouseEvent) => {
        // Also trigger on mouse leave if cursor is near the top
        if (e.clientY <= 20 && !hasTriggered) {
          hasTriggered = true;
          setLocalData((prev: ExitIntentFormLocalData) => ({
            ...prev,
            lastTriggeredAt: new Date(),
          }));
          openModal().catch((e) =>
            console.error("Failed to open exit intent form", e),
          );
        }
      };

      // Add event listeners
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseleave", handleMouseLeave);
      };
    }, [
      form,
      formId,
      completed,
      localData,
      setLocalData,
      openModal,
      isValidURLtoShowForm,
      open,
    ]);

    useEffect(() => {
      props.onShouldDisplayChanged?.(shouldDisplay);
    }, [shouldDisplay, props]);

    if (!form) return null;

    return (
      <MerchantCustomizableModal
        backdropColor={form.theme.overlay.color + opacityHex}
        fullscreen={form.layout.type === "fullscreen"}
        onClose={() => setOpen(false)}
        open={open && !completed}
      >
        <CustomFieldProvider
          getCustomFieldOptions={(id) =>
            client?.getCustomFieldOptions({ customFieldId: id }) ??
            Promise.resolve({ name: "", options: [] })
          }
        >
          <SignupForm
            brandKit={props.brandKit}
            currentStep={currentStep}
            form={form}
            onClose={() => setOpen(false)}
            onSubmitStep={async (
              i: number,
              formData: Record<string, string>,
            ) => {
              const entryPoint = props.entryPoint;
              await submitFormStep({
                i,
                formData,
                form,
                formId,
                client,
                customerId: props.customerId,
                localData,
                logAnalytics,
                setCurrentStep,
                entryPoint,
                skipPhoneSteps: formLoad.value?.skipPhoneSteps ?? true,
                onLastStepSubmitted: () => {
                  setOpen(false);
                  setCompleted(true);
                },
              });
            }}
          />
        </CustomFieldProvider>
      </MerchantCustomizableModal>
    );
  },
);

function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    const item = localStorage.getItem(key);
    return item ? (parseLocalData(item) as T) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(storedValue));
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}
